/*
 * RadioGroup with two looks:
 *   <RadioGroup value={v} onValueChange={setV} options={[{ value, label, description }]} />        plain list
 *   <RadioCards value={os} onValueChange={setOs} options={[{ value, label, description, icon }]} /> cards
 * Arrow keys move the selection (Radix), in reading order for both directions.
 */
import { RadioGroup as R } from 'radix-ui'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type RadioOption = {
  value: string
  label: ReactNode
  description?: ReactNode
  icon?: ReactNode
  /** content on the end side of a card (a price, a badge) */
  aside?: ReactNode
  disabled?: boolean
}

type GroupProps = {
  value?: string
  onValueChange: (v: string) => void
  options: RadioOption[]
  name?: string
  disabled?: boolean
  className?: string
  'aria-label'?: string
  'aria-labelledby'?: string
}

function Dot() {
  return (
    <span className="grid size-5 shrink-0 place-items-center rounded-full border border-hairline-strong bg-surface shadow-[0_1px_1px_var(--color-control)] transition-colors group-data-[state=checked]:border-brand-600 group-data-[state=checked]:bg-brand-600">
      <span className="size-2 scale-0 rounded-full bg-white transition-transform group-data-[state=checked]:scale-100" />
    </span>
  )
}

export function RadioGroup({
  value,
  onValueChange,
  options,
  name,
  disabled,
  className,
  ...aria
}: GroupProps) {
  return (
    <R.Root
      value={value}
      onValueChange={onValueChange}
      name={name}
      disabled={disabled}
      className={cn('flex flex-col gap-3', className)}
      {...aria}
    >
      {options.map((o) => (
        <R.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          className="group flex items-start gap-3 rounded-lg text-start outline-offset-4 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="mt-0.5">
            <Dot />
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-14 leading-6 text-ink">{o.label}</span>
            {o.description ? <span className="text-13 text-subtle">{o.description}</span> : null}
          </span>
        </R.Item>
      ))}
    </R.Root>
  )
}

export function RadioCards({
  value,
  onValueChange,
  options,
  name,
  disabled,
  className,
  columns = 3,
  ...aria
}: GroupProps & { columns?: 1 | 2 | 3 | 4 }) {
  const cols = { 1: '', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[
    columns
  ]
  return (
    <R.Root
      value={value}
      onValueChange={onValueChange}
      name={name}
      disabled={disabled}
      className={cn('grid gap-3', cols, className)}
      {...aria}
    >
      {options.map((o) => (
        <R.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled}
          className={cn(
            'group relative flex items-start gap-3 rounded-2xl bg-surface p-4 text-start ring-1 ring-card-line transition-[box-shadow,background-color] duration-150 ring-inset',
            'hover:ring-card-line-hover data-[state=checked]:bg-brand-50/50 data-[state=checked]:ring-2 data-[state=checked]:ring-brand-600',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {o.icon ? (
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-surface-tint text-brand-600 ring-1 ring-hairline group-data-[state=checked]:bg-white [&_svg]:size-5">
              {o.icon}
            </span>
          ) : null}
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-14 font-semibold text-ink">{o.label}</span>
            {o.description ? <span className="text-13 leading-5 text-muted">{o.description}</span> : null}
          </span>
          {o.aside ? <span className="shrink-0 text-14 text-ink">{o.aside}</span> : null}
          <span className="shrink-0">
            <Dot />
          </span>
        </R.Item>
      ))}
    </R.Root>
  )
}
